import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import { CreditCard, Truck, MessageCircle, Loader2, Tag, X } from "lucide-react";
import api, { inr, formatApiErrorDetail, WHATSAPP } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("cod");
  const [couponCode, setCouponCode] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount }
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    notes: "",
  });

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const discount = applied?.discount || 0;
  const payable = Math.max(0, subtotal - discount);

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/apply", { code, subtotal });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied — you save ${inr(data.discount)}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not apply coupon");
    } finally {
      setApplying(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-5 py-28 text-center" data-testid="checkout-empty">
        <h1 className="text-3xl text-primary font-medium">Your cart is empty</h1>
        <button onClick={() => navigate("/shop")} className="mt-6 bg-primary text-primary-foreground px-8 py-3 text-sm">
          Go to Shop
        </button>
      </div>
    );
  }

  const buildPayload = () => ({
    customer_name: form.customer_name,
    email: form.email,
    phone: form.phone,
    shipping: { address: form.address, city: form.city, state: form.state, pincode: form.pincode },
    items: items.map((i) => ({
      product_id: i.product_id,
      name: i.name,
      price: i.price,
      qty: i.qty,
      image: i.image,
      variant: i.variant || "",
    })),
    payment_method: method,
    notes: form.notes,
    coupon_code: applied?.code || null,
  });

  const validate = () => {
    for (const f of ["customer_name", "email", "phone", "address", "city", "state", "pincode"]) {
      if (!form[f].trim()) {
        toast.error("Please fill in all delivery details");
        return false;
      }
    }
    return true;
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", buildPayload());

      if (method === "cod") {
        clearCart();
        navigate("/order-confirmation", { state: { order } });
        return;
      }

      // Razorpay
      const options = {
        key: order.razorpay_key_id,
        amount: Math.round(order.total * 100),
        currency: "INR",
        name: "Aethel Decor",
        description: `Order ${order.order_number}`,
        order_id: order.razorpay_order_id,
        prefill: { name: form.customer_name, email: form.email, contact: form.phone },
        theme: { color: "#1A3626" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify-payment", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clearCart();
            navigate("/order-confirmation", { state: { order: { ...order, payment_status: "paid", status: "Confirmed" } } });
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not place order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="checkout-page">
      <h1 className="text-4xl text-primary font-medium mb-10">Checkout</h1>
      <form onSubmit={placeOrder} className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          {/* Contact + shipping */}
          <section>
            <h2 className="font-display text-xl text-primary mb-5">Delivery details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Full name</span>
                <input type="text" value={form.customer_name} onChange={set("customer_name")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-customer_name" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Phone</span>
                <input type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-phone" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email</span>
                <input type="email" value={form.email} onChange={set("email")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-email" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Pincode</span>
                <input type="text" value={form.pincode} onChange={set("pincode")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-pincode" />
              </label>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Address</span>
                  <input type="text" value={form.address} onChange={set("address")} placeholder="House no., street, area" className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-address" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">City</span>
                <input type="text" value={form.city} onChange={set("city")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-city" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">State</span>
                <input type="text" value={form.state} onChange={set("state")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-state" />
              </label>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Order notes (optional)</span>
                  <input type="text" value={form.notes} onChange={set("notes")} className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary" data-testid="checkout-notes" />
                </label>
              </div>
            </div>
          </section>

          {/* Payment */}
          <section>
            <h2 className="font-display text-xl text-primary mb-5">Payment method</h2>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setMethod("cod")}
                className={`w-full flex items-center gap-4 p-5 border text-left transition-colors ${
                  method === "cod" ? "border-primary bg-secondary/50" : "border-border hover:border-primary/50"
                }`}
                data-testid="pay-cod"
              >
                <Truck size={22} className="text-primary" />
                <div>
                  <p className="font-semibold text-foreground">Cash on Delivery</p>
                  <p className="text-sm text-muted-foreground">Pay in cash when your order arrives</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMethod("razorpay")}
                className={`w-full flex items-center gap-4 p-5 border text-left transition-colors ${
                  method === "razorpay" ? "border-primary bg-secondary/50" : "border-border hover:border-primary/50"
                }`}
                data-testid="pay-razorpay"
              >
                <CreditCard size={22} className="text-primary" />
                <div>
                  <p className="font-semibold text-foreground">Pay Online (Razorpay)</p>
                  <p className="text-sm text-muted-foreground">UPI, cards, netbanking & wallets</p>
                </div>
              </button>
            </div>
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
            >
              <MessageCircle size={16} /> Prefer to order on WhatsApp? Message us
            </a>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-secondary/50 border border-border p-6 lg:sticky lg:top-28">
            <h2 className="font-display text-xl text-primary mb-5">Your order</h2>
            <div className="space-y-4 max-h-64 overflow-auto pr-1">
              {items.map((i, idx) => (
                <div key={`${i.product_id}-${i.variant || "d"}-${idx}`} className="flex gap-3">
                  <div className="w-14 aspect-[4/5] bg-muted overflow-hidden shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-tight line-clamp-2">{i.name}</p>
                    <p className="text-xs text-muted-foreground">Qty {i.qty}{i.variant ? ` · ${i.variant}` : ""}</p>
                  </div>
                  <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border mt-4 pt-4">
              {applied ? (
                <div className="flex items-center justify-between bg-primary/10 px-3 py-2.5 text-sm" data-testid="coupon-applied">
                  <span className="flex items-center gap-2 text-primary font-semibold">
                    <Tag size={14} /> {applied.code}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setApplied(null); setCouponCode(""); }}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove coupon"
                    data-testid="coupon-remove"
                  >
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    className="flex-1 min-w-0 bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary uppercase"
                    data-testid="coupon-input"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applying || !couponCode.trim()}
                    className="border border-primary text-primary px-4 py-2 text-sm hover:bg-secondary transition-colors disabled:opacity-50"
                    data-testid="coupon-apply-btn"
                  >
                    {applying ? <Loader2 size={15} className="animate-spin" /> : "Apply"}
                  </button>
                </div>
              )}
            </div>
            <div className="border-t border-border mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-semibold">{inr(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-primary" data-testid="coupon-discount-row">
                  <span>Coupon discount</span>
                  <span className="font-semibold">− {inr(discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-primary font-semibold">Free</span>
              </div>
              <div className="flex justify-between text-base pt-2 border-t border-border">
                <span className="font-semibold">Total</span>
                <span className="font-semibold text-primary" data-testid="checkout-total">{inr(payable)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full bg-primary text-primary-foreground py-4 text-sm tracking-wide hover:opacity-95 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="place-order-btn"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {method === "cod" ? "Place Order" : `Pay ${inr(payable)}`}
            </button>
            {!user && (
              <p className="mt-3 text-xs text-center text-muted-foreground">
                Checking out as guest. <span className="text-primary">Login</span> to save your order history.
              </p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
